import React, { useState } from "react";
import ReactDOM from "react-dom";
import {
  Table,
  ButtonGroup,
  DropdownToggle,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  Badge
} from "reactstrap";
import FooterPagination from "../../Pagination/FooterPagination";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DASHBOARD_USER_ROLES, textRoles } from "../../../constants";
import {
  deleteEmployeeByEmployerId,
  editStatusEmployee,
  resendEmployeeInvite
} from "../../../slices/general/thunk";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import {
  statusToBadgeColor,
  EMPLOYEE_STATUS,
  employeeStatusLabels
} from "../../../constants";
import { useCurrentUser } from "../../../slices/auth/hooks";
import { getLabelForActiveTab } from "../../../utils/utils";
import EditStatusEmployeeModal from "../../modals/EmployeesModal/EditStatusEmployeeModal";
import UserStatusBadge from "../../Badges/UserStatusBadge";

const EmployeesTable = ({
  onEditClick,
  data,
  isTerminated,
  paginationProps,
  onRefreshTable,
  activeTab,
  openModalAddEmployee,
  shouldShowImportMessage,
  searchParam,
  searchDone
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, id } = useParams();
  const currentUser = useCurrentUser();

  console.log("data:", data);

  const [openModalEditStatus, setOpenModalEditStatus] = useState(false);
  const [itemToEditStatus, setItemToEditStatus] = useState(null);

  const isNotSearchResult = searchParam && !data.length && searchDone;
  const noData = data.length === 0 && !searchParam;
  const currentRole = currentUser.role;
  const isAdmin = currentRole === DASHBOARD_USER_ROLES.ADMIN;
  const isProfilePage = location.pathname.includes("/" + id);
  const isIndividualPage = location.pathname.includes("/individual");

  const toggleModalEditStatus = (item) => {
    setItemToEditStatus(item);
    setOpenModalEditStatus(!openModalEditStatus);
  };

  const handleEditClick = (item) => {
    onEditClick(item);
  };

  const isEmployeeInvited = employee => {
    return employee.status === 'active' && !employee.employeeCodeUsed;
  };

  const handleViewProfile = (item) => {
    navigate(`/employee/profile/${item.id}`);
  };

  const handleResendInvitation = async (item) => {
    try {
      const response = await dispatch(
        resendEmployeeInvite({
          employerId: item.employerId,
          employeeId: item.id,
        })
      ).unwrap();

      if (response.error) {
        Swal.fire({
          title: "Oops!",
          text: "There was a problem. Please try again later.",
          icon: "error",
          confirmButtonText: "Ok",
        });
        return;
      }

      Swal.fire(
        "Success!",
        `Invitation resent successfully to ${item.email}.`,
        "success"
      );
      onRefreshTable();
    } catch (err) {
      console.error(err);
      Swal.fire("Oops!", err.message, "error");
    }
  };

  const handleTerminateEmployee = async (item) => {
    Swal.fire({
      title: `Are you sure you want to terminate the employee with email ${item.email}?`,
      text: "The employee will be terminated on next billing period.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, terminate",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          Swal.fire({
            title: "Terminating employee...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            showConfirmButton: false,
          });

          const response = await dispatch(
            editStatusEmployee({
              employerId: item.employerId,
              employeeId: item.id,
              status: "deactivate",
            })
          ).unwrap();

          if (response.error) {
            Swal.fire({
              title: "Oops!",
              text: "There was a problem. Please try again later.",
              icon: "error",
              confirmButtonText: "Ok",
            });
            return;
          }

          Swal.fire(
            "Success!",
            `Employee ${item.email} has been terminated successfully.`,
            "success"
          );
          onRefreshTable();
        } catch (err) {
          console.error(err);
          Swal.fire("Error!", err.message, "error");
        }
      }
    });
  };

  const handleReactivateEmployee = async (item) => {
    Swal.fire({
      title: `Are you sure you want to reactivate the employee with email ${item.email}?`,
      text: "The employee will be reactivated on next billing period.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reactivate",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          Swal.fire({
            title: "Reactivating employee...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            showConfirmButton: false,
          });

          const response = await dispatch(
            editStatusEmployee({
              employerId: item.employerId,
              employeeId: item.id,
              status: "reactivate",
            })
          ).unwrap();

          if (response.error) {
            Swal.fire({
              title: "Oops!",
              text: "There was a problem. Please try again later.",
              icon: "error",
              confirmButtonText: "Ok",
            });
            return;
          }

          Swal.fire(
            "Success!",
            `Employee ${item.email} has been reactivated successfully.`,
            "success"
          );
          onRefreshTable();
        } catch (err) {
          console.error(err);
          Swal.fire("Error!", err.message, "error");
        }
      }
    });
  };

  const handleRemoveEmployee = async (item) => {
    Swal.fire({
      title: `Are you sure you want to remove Employee with email ${item.email}?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          Swal.fire({
            title: "Deleting...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
            showConfirmButton: false,
          });

          const response = await dispatch(
            deleteEmployeeByEmployerId({
              employerId: item.employerId,
              employeeId: item.id,
            })
          ).unwrap();

          if (response.error) {
            Swal.fire({
              title: "Oops!",
              text: `There was a problem deleting the employee: ${response.error}.`,
              icon: "error",
              confirmButtonText: "Ok",
            });
            return;
          }

          Swal.fire(
            "Deleted!",
            `Employee ${item.email} has been deleted successfully.`,
            "success"
          );
          onRefreshTable();
        } catch (err) {
          console.error(err);
          Swal.fire("Error!", err.message || "An error occurred", "error");
        }
      }
    });
  };

  const renderDropdownMenu = (item) => {
    const isInvited = isEmployeeInvited(item);
    const portalRoot = document.getElementById("portal-root");

    return portalRoot ? ReactDOM.createPortal(
      <DropdownMenu>
        <DropdownItem
          onClick={() => handleViewProfile(item)}
          className="edit-item-btn"
        >
          <i className="ri-eye-fill align-bottom me-2 text-muted"></i>
          View
        </DropdownItem>

        {!isTerminated && (
          <DropdownItem
            onClick={() => handleEditClick(item)}
            className="edit-item-btn"
          >
            <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
            Edit
          </DropdownItem>
        )}

        {!isTerminated && (
          <DropdownItem
            onClick={() => handleTerminateEmployee(item)}
            className="d-flex align-items-center"
          >
            <i className="ri-close-line ms-n1 me-2 fs-4 text-muted"></i>
            Terminate
          </DropdownItem>
        )}

        {isTerminated && (
          <DropdownItem
            onClick={() => handleReactivateEmployee(item)}
            className="d-flex align-items-center"
          >
            <i className="ri-information-line ms-n1 me-2 fs-4 text-muted"></i>
            Reactivate
          </DropdownItem>
        )}

        {isTerminated && (
          <DropdownItem
            onClick={() => handleRemoveEmployee(item)}
            className="remove-item-btn"
          >
            <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>
            Delete Completely
          </DropdownItem>
        )}

        {isInvited && isAdmin && (
          <DropdownItem
            onClick={() => handleResendInvitation(item)}
          >
            <i className="ri-mail-fill align-bottom me-2 text-muted"></i>
            Resend Invitation
          </DropdownItem>
        )}

        {isAdmin && (
          <DropdownItem onClick={() => toggleModalEditStatus(item)}>
            <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
            Edit Status
          </DropdownItem>
        )}
      </DropdownMenu>,
      portalRoot
    ) : null;
  };

  const employerColumn = {
    header: "Employer",
    accessor: "employerInfo",
    render: (item) => {
      const employer = item.employerInfo || item.employer;
      return (
        <span>
          {employer?.companyName ||
            employer?.firstName && employer.lastName ? (
            <strong>{employer?.companyName}</strong>
          ) : null}

        </span>
      );
    },
  };

  const columns = [
    {
      header: "First Name",
      accessor: "firstName",
    },
    {
      header: "Last Name",
      accessor: "lastName",
    },
    {
      header: "Email",
      accessor: "email",
      render: (item) => item.email || "",
    },
    {
      header: `${isIndividualPage ? "Individual" : "Employee"} Code`,
      accessor: "employeeCode",
      render: (item) => item.employeeCode || "",
    },
    {
      header: `${isIndividualPage ? "Individual" : "Employer"} Code`,
      accessor: "employerCode",
      render: (item) => item.employerCode || "",
    },
    {
      header: "Status",
      accessor: "status",
      render: (item) => {
        const label = employeeStatusLabels[item.status] || "Unknown";
        const badgeColor = statusToBadgeColor[item.status] || "secondary";
        const isInvited = isEmployeeInvited(item);

        if (isInvited &&
          isAdmin
        ) {
          return (
            <div className="d-flex flex-column align-items-start">
              <UserStatusBadge
                color="warning"
                status="Invited"
                fontSize="11"
              />
            </div>
          );
        }

        return (
          <div className="d-flex flex-column align-items-start">
            {item.active && label.toLowerCase() !== EMPLOYEE_STATUS.CurrentlyActive && (
              <UserStatusBadge status="Active" fontSize="11" />
            )}
            {(!item.active || label !== "active") && (
              <div className="mt-1">
                <UserStatusBadge
                  status={label}
                  color={badgeColor}
                  fontSize="11"
                />
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: "Tier",
      accessor: "tier", // Changed from "isFamily" to "tier"
      render: (item) => {
        return (
          <Badge color={item.tier === "F" ? "info" : "secondary"} pill className="font-size-11">
            {item.tier || "E"}
          </Badge>
        );
      },
    },
    {
      header: "Actions",
      accessor: "actions",
      render: (item) => {
        return (
          <ButtonGroup onClick={(e) => e.stopPropagation()}>
            <UncontrolledDropdown className="dropdown d-inline-block">
              <DropdownToggle tag="button" className="btn btn-soft-primary btn-sm">
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              {renderDropdownMenu(item)}
            </UncontrolledDropdown>
          </ButtonGroup>
        );
      },
    },
  ];

  // Insert employer column if not on profile page
  if (!isProfilePage) {
    columns.splice(3, 0, employerColumn);
  }

  const handleOpenModal = () => {
    openModalAddEmployee();
  };

  const activeTabLabel = getLabelForActiveTab(activeTab);
  const message = isIndividualPage
    ? "You have no individuals yet."
    : id && role
      ? `${textRoles[role]} has no ${activeTabLabel === "terminated" ? "terminated" : ""} employees yet.`
      : `You have no ${activeTabLabel} employees yet.`;

  return (
    <>
      <EditStatusEmployeeModal
        open={openModalEditStatus}
        setOpen={setOpenModalEditStatus}
        item={itemToEditStatus}
        onRefreshTable={onRefreshTable}
      />

      <Table className="table table-hover border align-middle table-nowrap mb-0">
        <thead className="bg" style={{ background: "var(--thead-bg)" }}>
          <tr>
            {columns.map((column, index) => (
              <th key={index} scope="col">
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isNotSearchResult && (
            <tr>
              <td className="text-center" colSpan={columns.length}>
                No search results for "{searchParam}"
              </td>
            </tr>
          )}
          {noData ? (
            <tr>
              <td className="text-center" colSpan={columns.length}>
                {message}
                <br />
                {shouldShowImportMessage && (currentRole === "employer" || isProfilePage) && (
                  <span
                    className="text-primary cursor-pointer"
                    onClick={() => handleOpenModal()}
                  >
                    Import from CSV
                  </span>
                )}
              </td>
            </tr>
          ) : (
            data.map((item, index) => (
              <tr key={index}>
                {columns.map((column, colIndex) => (
                  <td
                    className="cursor-pointer"
                    onClick={() => handleViewProfile(item)}
                    key={colIndex}
                  >
                    {column.render
                      ? column.render(item)
                      : item[column.accessor]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
        {paginationProps.totalItems ? (
          <tfoot>
            <tr>
              <td colSpan={columns.length}>
                <FooterPagination
                  itemsCount={paginationProps.itemsCount}
                  totalPages={paginationProps.totalPages}
                  totalItems={paginationProps.totalItems}
                  currentPage={paginationProps.currentPage}
                  onChangePage={paginationProps.onChangePage}
                  onChangePageSize={paginationProps.onChangePageSize}
                />
              </td>
            </tr>
          </tfoot>
        ) : null}
      </Table>
    </>
  );
};

export default EmployeesTable;